/**
 * ACL routes — the `route` argument passed to acl-svc `myPermissions(route)`.
 *
 * Each route maps to a key namespace owned by the BE CSV catalogue. Only the
 * `GLOBAL` route (sidebar routing keys) exists today; per-screen routes
 * (e.g. ENTITY → `entities:*`) are added as the catalogue grows.
 *
 * Modelled as a `const` object + union type (the project enables
 * `erasableSyntaxOnly`, which forbids TS `enum`) — usage is identical:
 * `AclRoute.GLOBAL` for the value, `AclRoute` for the type.
 */
export const AclRoute = {
  /** Top-level app shell — sidebar routing keys (`routing:*`). */
  GLOBAL: 'GLOBAL',
  /** Agencies screen — agency-scoped keys (`agencies:*`). */
  AGENCY: 'AGENCY',
  /** Clients screen — client-scoped keys (`clients:*`). */
  CLIENT: 'CLIENT',
  /** Vendors screen — vendor-scoped keys (`vendors:*`). */
  VENDOR: 'VENDOR',
  /** Workers screen — worker-scoped keys (`workers:*`). */
  WORKER: 'WORKER',
  /** Entities screen — entity-scoped keys (`entities:*`). */
  ENTITY: 'ENTITY',
  /** People screen — natural-person-scoped keys (`people:*`). */
  PERSON: 'PERSON',
  /** Engagements screen — engagement-scoped keys (`engagements:*`). */
  ENGAGEMENT: 'ENGAGEMENT',
  /** Template Library admin screen — template authoring keys (`form_library:*`). ENT-325.
   *  Route name matches acl-svc CSV row (ui-permissions.csv:36: TEMPLATE_LIBRARY,form_library:write). */
  TEMPLATE_LIBRARY: 'TEMPLATE_LIBRARY',
  /** Invoices module — raise/approve/hold + the custom-field and bank-file admin gates
   *  (`invoices:*` / `invoice:*`). The BE authorizes every real call; these only shape the UI. */
  INVOICE: 'INVOICE',
  /** Compliance tabs — curation/manage verbs (`compliance:*`). CPR.
   *  Gates the [MGR] verbs (track/dismiss/skip/assign/profile/ad-hoc/digest); the BE
   *  authorizes every real call (FORBIDDEN without the key). */
  COMPLIANCE: 'COMPLIANCE',
  /** Settings → Team Members — view/manage-team keys (`team:*`). ENT-425.
   *  Route name matches acl-svc CSV row (ui-permissions.csv: TEAM,team:read / TEAM,team:write). */
  TEAM: 'TEAM',
  /** Time tracking — time logs, timesheets, time off, leave policies, holiday calendars
   *  (`time:*`). One route, deliberately: the source app split this into TIME_LOGS / TIME_OFF and
   *  that split is why it ended up with two inconsistent key prefixes. Keys gate CAPABILITY;
   *  per-worker row visibility is a separate server-side grant (D-5). */
  TIME_TRACKING: 'TIME_TRACKING',
  /** Agreements (B2B contracts) surface — create/send/sign/decline verbs (`contracts:*`).
   *  Backed by contract-svc (prefix `CT_`); the BE authorizes every real call. Keys still need
   *  seeding in acl-svc `ui-permissions.csv` (BE follow-up) before they resolve. */
  CONTRACTS: 'CONTRACTS',
  /** Reports Workspace v2 (ENT-466) — save/delete/favourite verbs (`reports:*`). The BE authorizes
   *  every real call (`@RequirePermission('reports', 'write')`, resolvers in entity-management-svc).
   *  Keys still need seeding in acl-svc `ui-permissions.csv` (BE follow-up, confirmed absent as of
   *  T02 — same gap pattern as CONTRACTS above) before this route resolves anything but "denied". */
  REPORTS: 'REPORTS',
  /** Help Requests (support chat) surface — read/create/process/invite verbs (`help_request:*`).
   *  Backed by chat-integration-svc (prefix `CI_`); the BE authorizes every real call. Unlike
   *  CONTRACTS and REPORTS above, the keys ARE seeded — acl-svc migration
   *  `20260922100004-seed-help-request-ui-map.ts` writes rows for PLATFORM_ADMIN / AGENCY /
   *  CLIENT / VENDOR — so this route resolves rather than always denying.
   *  Added because acl-svc's `ui-permission-map-route-values.acl-route-parity` spec failed on
   *  `route='HELP_REQUEST'` not existing here: the BE seeds the rows, and without this member
   *  every one of them is dead on arrival. Note that spec self-skips when entite-web is not on
   *  disk (i.e. always in CI), so this class of break is only ever caught locally. */
  HELP_REQUEST: 'HELP_REQUEST',
  /** Accounting integrations — scope listing and connect/manage actions. */
  INTEGRATIONS: 'INTEGRATIONS',
  /** Settings > Branding — the tenant brand kit (`branding:configure`). form-svc authorizes every call;
   *  acl-svc grants the key to `agency-admin` / `client-admin` (AGENCY and CLIENT personas only). */
  BRANDING: 'BRANDING',
  /** Agency CRM tool (`/crm/*`, ENT-565) — one key per crm-svc permission (`crm:*`).
   *  crm-svc authorizes every real call; see `keys/crm.acl_keys.ts` for the mapping rules. */
  CRM: 'CRM',
} as const;

export type AclRoute = (typeof AclRoute)[keyof typeof AclRoute];
